import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Phone, Mail, User, Briefcase, MessageSquare, DollarSign, ChevronDown } from "lucide-react";

export const ConsultationModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    service: "",
    phone: "",
    description: "",
    budget: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Services list from whatwedo
  const services = [
    "AI-Powered Automation",
    "Data Scraping",
    "Frontend Development",
    "Backend Development",
    "AI & ML Solutions",
    "Full-Stack Development",
    "Other",
  ];

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError("");
    let submissionSucceeded = false;
    const honeyValue = e.currentTarget.elements._honey?.value?.trim?.() || "";

    // Honeypot trap: bots often fill hidden fields.
    if (honeyValue) {
      setIsSubmitting(false);
      return;
    }

    const submissionData = new FormData();
    submissionData.append(
      "_subject",
      `New Consultation Request - ${formData.name}`
    );
    submissionData.append("name", formData.name);
    submissionData.append("email", formData.email);
    submissionData.append("phone", formData.phone);
    submissionData.append("service", formData.service);
    submissionData.append("budget", formData.budget);
    submissionData.append("description", formData.description);
    submissionData.append("_captcha", "false");
    submissionData.append("_template", "table");
    submissionData.append("_honey", honeyValue);

    try {
      const response = await fetch("https://formsubmit.co/ajax/hello@jamilglobal.com", {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: submissionData,
      });

      if (!response.ok) {
        throw new Error("Form submission failed");
      }

      setIsSubmitted(true);
      submissionSucceeded = true;
    } catch (error) {
      setSubmitError(
        "Could not send your request right now. Please try again in a moment."
      );
    } finally {
      setIsSubmitting(false);
    }

    // Auto close after success message
    if (submissionSucceeded) {
      setTimeout(() => {
        onClose();
        // Reset form after a delay so the reset isn't visible during close animation
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: "",
            email: "",
            service: "",
            phone: "",
            description: "",
            budget: "",
          });
        }, 500);
      }, 4000);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-[1100]"
            onClick={onClose}
          />

          {/* Modal Container */}
          <div className="fixed inset-0 z-[1101] flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl pointer-events-auto flex flex-col max-h-[85vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative flex flex-col h-full overflow-hidden rounded-3xl">
                {/* Header with Background */}
                <div className="bg-[#112917] p-8 text-white relative overflow-hidden flex-shrink-0">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16 blur-3xl"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full -ml-12 -mb-12 blur-2xl"></div>

                  <button
                    onClick={onClose}
                    className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>

                  <h2 className="text-2xl sm:text-3xl font-light mb-2">Schedule a Consultation</h2>
                  <p className="text-white/70 font-light text-xs sm:text-sm">Let's discuss how we can bring your vision to life.</p>
                </div>

                {/* Form Content */}
                <div className="p-8 md:p-10 bg-white overflow-y-auto custom-scrollbar">
                  <style>{`
                    .custom-scrollbar::-webkit-scrollbar {
                      width: 6px;
                    }
                    .custom-scrollbar::-webkit-scrollbar-track {
                      background: transparent;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb {
                      background-color: #e5e7eb;
                      border-radius: 20px;
                    }
                  `}</style>
                  {isSubmitted ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-12"
                    >
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Send className="w-10 h-10 text-[#112917]" />
                      </div>
                      <h3 className="text-2xl font-medium text-gray-900 mb-2">Thank You!</h3>
                      <p className="text-gray-600">Your message has been sent. We'll get back to you within 48 hours.</p>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <input
                        type="text"
                        name="_honey"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        className="hidden"
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Name Field */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            Your Name
                          </label>
                          <input
                            required
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="John Doe"
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#112917] focus:ring-2 focus:ring-[#112917]/10 outline-none transition-all"
                          />
                        </div>

                        {/* Email Field */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            <Mail className="w-4 h-4 text-gray-400" />
                            Your Email
                          </label>
                          <input
                            required
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="john@example.com"
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#112917] focus:ring-2 focus:ring-[#112917]/10 outline-none transition-all text-sm sm:text-base"
                          />
                        </div>

                        {/* Service Dropdown */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            <Briefcase className="w-4 h-4 text-gray-400" />
                            Service Needed
                          </label>
                          <div className="relative">
                            <select
                              required
                              name="service"
                              value={formData.service}
                              onChange={handleChange}
                              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#112917] focus:ring-2 focus:ring-[#112917]/10 outline-none transition-all appearance-none bg-white cursor-pointer text-sm sm:text-base"
                            >
                              <option value="" disabled>Select a service</option>
                              {services.map((service) => (
                                <option key={service} value={service}>{service}</option>
                              ))}
                            </select>
                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                          </div>
                        </div>

                        {/* Phone Number */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            Phone Number
                          </label>
                          <input
                            required
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+1 (555) 000-0000"
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#112917] focus:ring-2 focus:ring-[#112917]/10 outline-none transition-all text-sm sm:text-base"
                          />
                        </div>
                      </div>

                      {/* Budget Dropdown */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-gray-400" />
                          What is your budget?
                        </label>
                        <div className="relative">
                          <select
                            required
                            name="budget"
                            value={formData.budget}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#112917] focus:ring-2 focus:ring-[#112917]/10 outline-none transition-all appearance-none bg-white cursor-pointer"
                          >
                            <option value="" disabled>Select budget range</option>
                            <option value="<$5k">Less than $5,000</option>
                            <option value="$5k-$10k">$5,000 - $10,000</option>
                            <option value="$10k-$25k">$10,000 - $25,000</option>
                            <option value="$25k-$50k">$25,000 - $50,000</option>
                            <option value="$50k+">$50,000+</option>
                          </select>
                          <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                      </div>

                      {/* Project Description */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <MessageSquare className="w-4 h-4 text-gray-400" />
                          Describe your project
                        </label>
                        <textarea
                          required
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          placeholder="Tell us about your project goals and requirements..."
                          rows={4}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#112917] focus:ring-2 focus:ring-[#112917]/10 outline-none transition-all resize-none text-sm sm:text-base"
                        />
                      </div>

                      {/* Submit Button */}
                      {submitError && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-600 text-sm sm:text-base font-medium text-center"
                        >
                          {submitError}
                        </motion.div>
                      )}
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-[#112917] text-white py-4 rounded-xl font-medium text-sm sm:text-lg hover:opacity-95 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-[#112917]/20"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            Sending...
                          </>
                        ) : (
                          <>
                            Submit Request
                            <Send className="w-5 h-5" />
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ConsultationModal;
